
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnChanges, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective, FormsModule, Validators } from "@angular/forms";
import { NgForOf, NgIf } from "@angular/common";
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-combobox-multiselect',
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    NgForOf
  ],
  templateUrl: './combobox-multiselect.component.html',
  styleUrls: ['./combobox-multiselect.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class ComboboxMultiselectComponent implements OnInit, OnChanges, OnDestroy {

  @Input() label: string = 'Select options';
  @Input() controlName: string = '';
  @Input() options: Record<string, any>[] = [];
  @Input() required: boolean = false;
  @Input() showIcon: boolean = false;
  @Input() placeholder: string = 'Select options';
  @Input() displayKey: string = 'name';
  @Input() valueKey: string = 'id';
  @Input() maxSelection: number = Infinity;  // Default to no limit
  @Output() selectionChange = new EventEmitter<any[]>();

  filteredOptions: Record<string, any>[] = [];
  selectedOptions: Record<string, any>[] = [];
  isOpen = false;
  searchTerm = '';
  private clickListener: () => void;
  private valueChangeSub: Subscription | null = null;

  constructor(public controlContainer: FormGroupDirective, private elRef: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.initializeOptions();

    if (this.required) {
      this.formControl.setValidators(Validators.required);
      this.formControl.updateValueAndValidity();
    }

    // Subscribe to form control value changes to sync selectedOptions
    this.valueChangeSub = this.formControl.valueChanges.subscribe(() => {
      this.syncSelectedOptionsFromFormValue();
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && changes['options'].currentValue) {
      this.initializeOptions();  // Reinitialize options when input changes
    }
  }
  ngOnDestroy() {
    this.removeClickOutsideListener();
    if (this.valueChangeSub) {
      this.valueChangeSub.unsubscribe();
    }
  }

  get formControl(): FormControl {
    return this.controlContainer.control.get(this.controlName) as FormControl;
  }

  initializeOptions(): void {
    if (Array.isArray(this.options) && this.options.length > 0) {
      this.filteredOptions = [...this.options];
    } else {
      console.error('ComboboxMultiselectComponent: options array is undefined or empty.', this.options);
      this.filteredOptions = [];
    }

    // Initialize selectedOptions based on form control value
    this.syncSelectedOptionsFromFormValue();
  }

  // Sync selectedOptions with form control value (called on value changes)
  private syncSelectedOptionsFromFormValue(): void {
    if (!this.options || this.options.length === 0) {
      return; // Options not loaded yet
    }
    const selectedValueIds = Array.isArray(this.formControl.value) ? this.formControl.value : [];
    // Convert IDs to strings for comparison to handle type mismatches (string vs number)
    const selectedValueIdsAsStrings = selectedValueIds.map((id: any) => String(id));
    this.selectedOptions = this.options.filter(option =>
      selectedValueIdsAsStrings.includes(String(option[this.valueKey]))
    );
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.addClickOutsideListener();
    } else {
      this.removeClickOutsideListener();
    }
  }

  onSearchTermChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    // Typing must always reveal the list, even if a prior click had toggled it closed.
    if (!this.isOpen) {
      this.isOpen = true;
      this.addClickOutsideListener();
    }
    this.filterOptions();
  }

  filterOptions(): void {
    this.filteredOptions = this.options.filter(option =>
      option[this.displayKey]?.toString().toLowerCase().includes(this.searchTerm.toLowerCase())
    );

    if (this.filteredOptions.length === 0) {
      this.filteredOptions = [{ [this.displayKey]: 'Data not found.', [this.valueKey]: null }];
    }
  }

  toggleSelection(option: Record<string, any>): void {
    if (option[this.valueKey] === null) {
      return; // Do not select "Data not found." placeholder
    }

    // Use string comparison for consistency
    const index = this.selectedOptions.findIndex(
      selected => String(selected[this.valueKey]) === String(option[this.valueKey])
    );

    if (index === -1) {
      // Prevent further selection if the max selection limit is reached
      if (this.selectedOptions.length >= this.maxSelection) {
        return;
      }
      this.selectedOptions.push(option);
    } else {
      this.selectedOptions.splice(index, 1);
    }

    const selectedIds = this.selectedOptions.map(item => item[this.valueKey]);
    this.formControl.setValue(selectedIds);
    this.selectionChange.emit(this.selectedOptions);

    this.formControl.markAsTouched();
  }

  removeSelectedOption(option: Record<string, any>): void {
    // Use string comparison for consistency
    const index = this.selectedOptions.findIndex(
      selected => String(selected[this.valueKey]) === String(option[this.valueKey])
    );
    if (index !== -1) {
      this.selectedOptions.splice(index, 1);
      const selectedIds = this.selectedOptions.map(item => item[this.valueKey]);
      this.formControl.setValue(selectedIds);
      this.selectionChange.emit(this.selectedOptions);
    }
  }

  isSelected(option: Record<string, any>): boolean {
    // Use string comparison to handle type mismatches (string vs number)
    return Array.isArray(this.selectedOptions) && this.selectedOptions.some(
      selected => String(selected[this.valueKey]) === String(option[this.valueKey])
    );
  }

  private addClickOutsideListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (targetElement && !this.elRef.nativeElement.contains(targetElement)) {
        this.isOpen = false;
        this.searchTerm = '';
        this.filteredOptions = [...this.options];
        this.removeClickOutsideListener();
      }
    });
  }

  private removeClickOutsideListener() {
    if (this.clickListener) {
      this.clickListener();
      this.clickListener = null;
    }
  }

  get hasValue(): boolean {
    // Check if there are any selected options
    return Array.isArray(this.selectedOptions) && this.selectedOptions.length > 0;
  }

}
